/**
 * AdminShell — the top-level layout for the platform admin surface.
 *
 * Visually distinct from the tenant staff UI:
 *  - Slightly different background (warm amber tint instead of paper)
 *  - "PLATFORM" badge top-left
 *  - Simple horizontal nav: Dashboard / Accounts / Provision / Sign out
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

export function AdminShell({ children }: Props) {
  const location = useLocation();

  function navClass(path: string) {
    const active = location.pathname === path || location.pathname.startsWith(`${path}/`);
    return [
      'text-sm font-medium px-3 py-1.5 rounded transition-colors',
      active
        ? 'bg-[#8B3A00]/10 text-[#8B3A00]'
        : 'text-muted-foreground hover:text-[var(--color-text)]',
    ].join(' ');
  }

  return (
    <div className="min-h-[100dvh] bg-[oklch(97%_0.025_70)] flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-[oklch(95%_0.04_70)] border-b border-[oklch(88%_0.05_70)] px-4 flex items-center gap-4 h-12">
        {/* Platform badge */}
        <span className="text-[10px] font-bold tracking-widest uppercase bg-[#8B3A00] text-white px-2 py-0.5 rounded">
          PLATFORM
        </span>

        <nav className="flex items-center gap-1 ml-2">
          <Link to="/admin/dashboard" className={navClass('/admin/dashboard')}>
            Dashboard
          </Link>
          <Link to="/admin/accounts" className={navClass('/admin/accounts')}>
            Accounts
          </Link>
          <Link to="/admin/provision" className={navClass('/admin/provision')}>
            Provision
          </Link>
        </nav>

        <div className="ml-auto">
          <button
            type="button"
            onClick={() => {
              fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include' }).then(() => {
                window.location.href = '/login';
              });
            }}
            className="text-sm text-muted-foreground hover:text-[var(--color-text)] transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-6">{children}</main>
    </div>
  );
}
